import { firestore } from "@/services/firestore";
import { Role, User, EmployeeRole } from "@/types/v2";
import { shop } from "./shop";
import { TERMS_VERSION } from "@/lib/terms";
import { httpsCallables } from "@/repositories/httpsCallables";
import { authActions } from "@/services/auth";
import { generic } from "@/repositories/generic";

export const user = {
  getById: async (id: string) => {
    return firestore.getDocument<User>(`users/${id}`);
  },
  create: async (user: Partial<User>) => {
    return generic.create(user, `users/${user.id}`);
  },
  update: async (user: Partial<User>) => {
    return firestore.updateDocument(`users/${user.id}`, user);
  },
  subscribe: (id: string, callback: (doc: User | undefined) => void) => {
    return firestore.subscribeDocument<User>(`users/${id}`, callback);
  },
  markEmailVerified: (id: string) => {
    return user.update({ id: id, emailVerified: true });
  },
  getLinkedShops: async (id: string, role: Role) => {
    // Attach shops that user has access to
    let allowedShops;
    if (role === "superAdmin") {
      allowedShops = await shop.listShops();
    } else {
      allowedShops = await shop.listShopsByUser(id);
    }
    return allowedShops.filter((shop) => !shop.disabled);
  },
  agreeToTerms: async (id: string) => {
    await user.update({
      id: id,
      acceptedTermsAt: new Date(),
      acceptedTermsVersion: TERMS_VERSION,
    });
  },
  getId: (email: string) => {
    return httpsCallables.getAuthUser({ email });
  },
  createAuthAccount: async (
    email: string,
    firstName: string,
    lastName: string,
    shopId: string,
    employeeId: string
  ) => {
    const loginLink = `${window.location.origin}/login`;
    return httpsCallables.createAuthUser({
      email,
      displayName: `${firstName} ${lastName}`,
      shopId,
      employeeId,
      loginLink,
    });
  },
  deactivateAuthAccount: (email: string) => {
    return httpsCallables.deactivateAuthUser({ email });
  },
  /** Re-enables the employee's existing account; the password is unchanged. */
  reactivateAuthAccount: (shopId: string, employeeId: string) => {
    return httpsCallables.reactivateAuthUser({ shopId, employeeId });
  },
  updateUserRole: ({
    userId,
    userRole,
    userRoleEnabled,
    userShopId,
    disableUser,
  }: {
    userId: string;
    userRole?: EmployeeRole;
    userRoleEnabled?: boolean;
    userShopId?: string;
    disableUser?: boolean;
  }) => {
    return httpsCallables.updateUserClaims({
      userId,
      userRole,
      userRoleEnabled,
      userShopId,
      disableUser,
    });
  },
  getUserShopRole: async (
    shopId: string
  ): Promise<EmployeeRole | "superAdmin" | undefined> => {
    const claims = await authActions.getUserClaims(shopId);
    if (!claims) return undefined;

    if (claims.superAdmin) return "admin";

    const shopClaims = claims.shops ? claims.shops[shopId] : undefined;

    if (shopClaims && shopClaims.admin) {
      return "admin";
    } else if (shopClaims && shopClaims.office) {
      return "office";
    } else if (shopClaims && shopClaims.technician) {
      return "technician";
    } else if (shopClaims && shopClaims.viewOnly) {
      return "viewOnly";
    }
    // TODO: RESET TO viewOnly later
    return "admin";
  },
};
